<script lang="tsx">
import Decimal from 'decimal.js'
import { defineComponent, shallowRef, watch, type PropType, type WatchStopHandle } from 'vue'
import { createNamespace } from '@bd/vue3-utils'
import { RESETTLE_WAY, RESETTLE_WAY_LABEL } from '@/constant/biz'

const [name, bem] = createNamespace('JtZchResettleDisplay')
/**
 * 统规统建安置
 * -安置小区（每人）members*house.community.communityName
 * -安置时间（每人）members*commonPlanTime
 * 货币化安置
 * -金额（每人） members*currencyPrice
 * -打款时间（每人）members*currencyTime
 * --总金额（户）sum(members*currencyPrice)
 * 房票安置
 * --安置总价款（户） sum(members*ticketCommodityPrice)
 * --补差打款时间（户） ticketTime
 * 统规自建安置
 * --安置时间（户）personTime
 * --安置面积（户）personPlanArea
 */
export default defineComponent({
  name,
  props: {
    record: {
      type: Object as PropType<ApiJtZch.Item>
    }
  },
  setup(props) {
    // 存储按安置方式分组的家庭成员列表
    const groupList = shallowRef<Record<string, ApiFamilyMembers.Item[]>>({})

    /**
     * 初始化空的分组对象
     * @returns 包含所有安置方式的空分组对象
     */
    const initGroups = (): Record<string, ApiFamilyMembers.Item[]> => {
      return {
        [RESETTLE_WAY.CASH]: [],
        [RESETTLE_WAY.TICKET]: [],
        [RESETTLE_WAY.PERSON_PLAN]: [],
        [RESETTLE_WAY.COMMON_PLAN]: []
      }
    }

    /**
     * 将家庭成员按照安置方式进行分组
     * 注意：一个成员可能有多种安置方式
     * @param list - 家庭成员列表
     */
    const groupMembersByResettleWay = (list: ApiFamilyMembers.Item[]): void => {
      const groups = initGroups()

      // 遍历每个成员，根据其安置方式添加到对应分组
      list.forEach(item => {
        if (item[RESETTLE_WAY.CASH]) {
          groups[RESETTLE_WAY.CASH].push(item)
        }
        if (item[RESETTLE_WAY.TICKET]) {
          groups[RESETTLE_WAY.TICKET].push(item)
        }
        if (item[RESETTLE_WAY.PERSON_PLAN]) {
          groups[RESETTLE_WAY.PERSON_PLAN].push(item)
        }
        if (item[RESETTLE_WAY.COMMON_PLAN]) {
          groups[RESETTLE_WAY.COMMON_PLAN].push(item)
        }
      })

      groupList.value = groups
    }

    /**
     * 计算成员列表的金额总和
     * @param members - 成员列表
     * @param priceField - 价格字段名
     * @returns 总金额的 Decimal 对象
     */
    const calculateTotalPrice = (members: ApiFamilyMembers.Item[], priceField: keyof ApiFamilyMembers.Item): Decimal => {
      return members.reduce((sum, item) => {
        const price = item[priceField] as number | undefined
        return sum.plus(new Decimal(price || 0))
      }, new Decimal(0))
    }

    /**
     * 渲染单个货币化安置成员的信息
     * @param member - 家庭成员
     */
    const renderCashMember = (member: ApiFamilyMembers.Item) => {
      return (
        <div class={bem('row')}>
          <span>{member.userName}</span>
          <label>金额：{member.currencyPrice || 0} 元</label>
          <label>打款时间：{member.currencyTime || '-'}</label>
        </div>
      )
    }

    /**
     * 渲染货币化安置信息
     * @param members - 采用货币化安置的成员列表
     */
    const renderCash = (members: ApiFamilyMembers.Item[]) => {
      const total = calculateTotalPrice(members, 'currencyPrice')
      return (
        <div class={bem('item')}>
          <h1>{RESETTLE_WAY_LABEL[RESETTLE_WAY.CASH]}</h1>
          {members.map(renderCashMember)}
          <div class={bem('row')}>
            <label>总金额：{total.toString()} 元</label>
          </div>
        </div>
      )
    }

    /**
     * 渲染房票安置信息
     * @param members - 采用房票安置的成员列表
     */
    const renderTicket = (members: ApiFamilyMembers.Item[]) => {
      const total = calculateTotalPrice(members, 'ticketCommodityPrice')
      return (
        <div class={bem('item')}>
          <h1>{RESETTLE_WAY_LABEL[RESETTLE_WAY.TICKET]}</h1>
          <div class={bem('row')}>
            <label>安置总价款：{total.toString()} 元</label>
          </div>
          <div class={bem('row')}>
            <label>补差打款时间：{props.record?.ticketTime || '-'}</label>
          </div>
        </div>
      )
    }

    /**
     * 渲染统规自建安置信息
     * 此类型的信息是户级别的，不是成员级别
     */
    const renderPersonPlan = () => {
      return (
        <div class={bem('item')}>
          <h1>{RESETTLE_WAY_LABEL[RESETTLE_WAY.PERSON_PLAN]}</h1>
          <div class={bem('row')}>
            <label>安置时间：{props.record?.personTime || '-'}</label>
          </div>
          <div class={bem('row')}>
            <label>安置面积：{props.record?.personPlanArea || '-'} m²</label>
          </div>
        </div>
      )
    }

    /**
     * 渲染单个统规统建安置成员的信息
     * @param member - 家庭成员
     */
    const renderCommonPlanMember = (member: ApiFamilyMembers.Item) => {
      return (
        <div class={bem('row')}>
          <span>{member.userName}</span>
          <label>安置小区：{member.house?.community?.communityName || '-'}</label>
          <label>安置时间：{member.commonPlanTime || '-'}</label>
        </div>
      )
    }

    /**
     * 渲染统规统建安置信息
     * @param members - 采用统规统建安置的成员列表
     */
    const renderCommonPlan = (members: ApiFamilyMembers.Item[]) => {
      return (
        <div class={bem('item')}>
          <h1>{RESETTLE_WAY_LABEL[RESETTLE_WAY.COMMON_PLAN]}</h1>
          {members.map(renderCommonPlanMember)}
        </div>
      )
    }

    // 渲染函数映射表
    const RENDER_MAP: Record<string, (members: ApiFamilyMembers.Item[]) => JSX.Element> = {
      [RESETTLE_WAY.CASH]: renderCash,
      [RESETTLE_WAY.TICKET]: renderTicket,
      [RESETTLE_WAY.PERSON_PLAN]: renderPersonPlan,
      [RESETTLE_WAY.COMMON_PLAN]: renderCommonPlan
    }

    // 监听 record 变化，更新分组数据
    let stopWatch: WatchStopHandle | null = null
    stopWatch = watch(
      () => props.record,
      newValue => {
        if (newValue && newValue.member) {
          groupMembersByResettleWay(newValue.member)
          // 首次加载后停止监听
          if (stopWatch) {
            stopWatch()
            stopWatch = null
          }
        }
      },
      { immediate: true }
    )

    return () => (
      <div class={bem()}>
        {Object.entries(groupList.value).map(([key, members]) => {
          if (members.length === 0) return null
          return RENDER_MAP[key](members)
        })}
      </div>
    )
  }
})
</script>
<style lang="less">
.jt-zch-resettle-display {
  &__item {
    margin-bottom: @margin-xs;

    h1 {
      margin-bottom: @margin-xs;
      font-weight: 600;
      font-size: 14px;
      line-height: 14px;
    }
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: @margin-xs;
    align-items: center;
    margin-bottom: @margin-xss;

    span,
    label {
      font-size: 12px;
      line-height: 12px;
    }

    span {
      font-weight: 500;
    }
  }
}
</style>
